import React, { Fragment } from 'react';

import { Combobox } from '@headlessui/react';
import cn from 'classnames';

import styles from './ComboBox.module.css';
import { Icon, Icons } from '../icon/Icon';
import { Input } from '../input/Input';

type TComboBoxProps = {
  /**
   * Available options should be passed as children using
   * `<ComboBoxOption />` as direct children
   *
   * Other children can also be provided. It is recommended to use
   * `<ComboBoxElement />` as it ensure consistent formatting with the options
   */
  children: React.ReactNode;

  /**
   * Receive the value of the currently selected option
   * and transform it into a displayable string.
   *
   * If multiple values get selected, this will receive an array as value
   */
  displayValue?: (value: string) => string;

  /**
   * Callback to handle the query change of the input
   *
   * Use to synchronise the query with a store outside of the component
   */
  onQueryChange?: (query: string) => void;

  /**
   * Callback to handle the selection of an option
   *
   * Takes the value of the option as an argument.
   * If multiple values get selected, this will receive an array as value
   */
  onOptionSelect?: (value: string) => void;

  /**
   * Force open list of options
   */
  open?: boolean;

  /**
   * Provide a value to turn this into a controlled component.
   *
   * A value is the value of the currently selected option,
   * NOT the current query
   */
  value?: string;
} & Omit<Parameters<typeof Input>[number], 'type'>;

export const ComboBox = (props: TComboBoxProps) => {
  const {
    onOptionSelect,
    value,
    name,
    onQueryChange,
    displayValue,
    children,
    open: forceOpen,
    disabled,
    ...inputProps
  } = props;

  return (
    <Combobox<string>
      onChange={(value) => {
        onOptionSelect?.(value);
      }}
      value={value}
      name={name}
      disabled={disabled}
    >
      {({ open }) => {
        return (
          <>
            <Combobox.Input
              onChange={(event) => {
                onQueryChange?.(event.target.value);
              }}
              displayValue={displayValue}
              as={Fragment}
            >
              <Input name={name} autoComplete="off" {...inputProps} />
            </Combobox.Input>
            {(open || Boolean(forceOpen)) && (
              <div className={styles.optionsContainer}>
                <Combobox.Options static className={styles.options}>
                  {React.Children.map(children, (child) => {
                    if (!isComboBoxOption(child)) {
                      return child;
                    }

                    return (
                      <Combobox.Option
                        as={Fragment}
                        key={child.props.value}
                        value={child.props.value}
                        disabled={child.props.disabled}
                      >
                        {({ active, selected }) => {
                          // @ts-expect-error Ensure whatever we pass in can be turned into a child
                          return React.cloneElement(child, {
                            active,
                            selected,
                          });
                        }}
                      </Combobox.Option>
                    );
                  })}
                </Combobox.Options>
              </div>
            )}
          </>
        );
      }}
    </Combobox>
  );
};

/**
 * Accepted props for the combobox options
 */
type TComboBoxOptionProps = {
  /**
   * ClassName to append if the option is currently active
   * (currently hovered)
   */
  activeClassName?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;

  /**
   * Display icon to the left side of an option
   *
   * Provide the identifier of the icon of choice, or undefined
   */
  iconLeft?: (typeof Icons)[number];

  /**
   * Display icon to the right side of an option
   *
   * Provide the identifier of the icon of choice, or undefined
   */
  iconRight?: (typeof Icons)[number];
  value: string;
};

/**
 * @internal
 * Don't define, these will be added injected by headless ui
 */
type _TComboBoxOptionProps = {
  /**
   * Returns true if the option is currently hovered
   */
  active?: boolean;

  /**
   * Returns true if the option is currently selected
   */
  selected?: boolean;
};

function isComboBoxOption(
  child: React.ReactNode,
): child is React.ReactNode & { props: TComboBoxOptionProps } {
  if (!React.isValidElement(child)) return false;
  if (child?.props?.value === undefined) return false;

  return true;
}

/**
 * Display an option for the `<ComboBox />` component.
 *
 * It must be placed as a direct child of the `<ComboBox />`
 * otherwise it will break.
 */
export const ComboBoxOption = React.forwardRef<
  HTMLLIElement,
  TComboBoxOptionProps & _TComboBoxOptionProps
>(
  (
    {
      children,
      // Used to extract the variable and throw it away
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      value,
      active,
      // Used to extract the variable and throw it away
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selected,
      className,
      activeClassName,
      iconLeft,
      iconRight,
      ...props
    },
    ref,
  ) => {
    return (
      <li
        {...props}
        ref={ref}
        className={cn(styles.option, className, {
          [styles.activeOption]: active,
          [activeClassName as any]: active,
          [styles.leftIconPadding]: Boolean(iconLeft),
          [styles.rightIconPadding]: Boolean(iconRight),
        })}
      >
        {iconLeft && <Icon className={styles.iconLeft} icon={iconLeft} />}
        {children}
        {iconRight && <Icon className={styles.iconRight} icon={iconRight} />}
      </li>
    );
  },
);

ComboBoxOption.displayName = 'ComboBoxOption';

type TComboBoxElementProps = {
  children: React.ReactNode;
  className?: string;
};

/**
 * Component to display additional elements inside the `<ComboBox />`
 * if they should not be treated like `<ComboBoxOption />`, but have
 * identical formatting.
 */
export const ComboBoxElement = React.forwardRef<
  HTMLDivElement,
  TComboBoxElementProps
>(({ className, children }, ref) => {
  return (
    <div className={cn(styles.option, className)} ref={ref}>
      {children}
    </div>
  );
});

ComboBoxElement.displayName = 'ComboBoxElement';
